import { Box, Stack, Typography } from "@mui/material";
import { LabellingCoverageBucket } from "store/api/generatedApi";
import dayjs from "dayjs";
import { CoverageStackedChart } from "./CoverageStackedChart";
import { useTenantTranslation } from "hooks/formatters";
import { useLanguage } from "hooks/settings";

interface Props {
  buckets: LabellingCoverageBucket[];
  month: dayjs.Dayjs;
}

export const CoverageCalendar = ({ buckets, month }: Props) => {
  const { t } = useTenantTranslation();
  const language = useLanguage();

  const weekdayNames = Array.from({ length: 7 }, (_, i) =>
    dayjs("2023-01-02").locale(language).add(i, "day").format("ddd")
  );

  const bucketsByDay = new Map<string, LabellingCoverageBucket[]>();
  buckets.forEach((bucket) => {
    const day = dayjs.utc(bucket.start).format("YYYY-MM-DD");
    const existing = bucketsByDay.get(day) ?? [];
    bucketsByDay.set(day, [...existing, bucket]);
  });

  const firstDayOfMonth = month.utc(true).startOf("month");
  const lastDayOfMonth = firstDayOfMonth.endOf("month");
  const startOfCalendar = firstDayOfMonth.startOf("week");
  const endOfCalendar = lastDayOfMonth.endOf("week");

  const days: dayjs.Dayjs[] = [];
  let current = startOfCalendar;
  while (
    current.isBefore(endOfCalendar) ||
    current.isSame(endOfCalendar, "day")
  ) {
    days.push(current);
    current = current.add(1, "day");
  }

  const weeks: dayjs.Dayjs[][] = [];
  for (let i = 0; i < days.length; i += 7) {
    weeks.push(days.slice(i, i + 7));
  }

  return (
    <Stack spacing={2}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(7, 1fr)",
          gap: 0,
        }}
      >
        {weekdayNames.map((dayName) => (
          <Typography
            key={dayName}
            variant="caption"
            align="center"
            fontWeight="bold"
            sx={{ py: 1 }}
          >
            {dayName}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "rgba(0, 0, 0, 0.08)",
        }}
      >
        {weeks.map((week) => {
          const weekBuckets: LabellingCoverageBucket[][] = week.map((day) => {
            const dayKey = day.format("YYYY-MM-DD");
            return bucketsByDay.get(dayKey) ?? [];
          });

          const weekKey = week[0]?.format("YYYY-MM-DD") ?? "";

          return (
            <Box
              key={weekKey}
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                gap: 0,
              }}
            >
              {week.map((day, idx) => {
                const dayKey = day.format("YYYY-MM-DD");
                const dayBuckets = weekBuckets[idx] ?? [];
                const isCurrentMonth = day.month() === month.month();

                return (
                  <Box
                    key={dayKey}
                    sx={{
                      position: "relative",
                      aspectRatio: "1",
                      borderRight: "1px solid",
                      borderBottom: "1px solid",
                      borderColor: "rgba(0, 0, 0, 0.08)",
                      bgcolor: isCurrentMonth
                        ? "background.paper"
                        : "action.hover",
                      opacity: isCurrentMonth ? 1 : 0.3,
                      overflow: "hidden",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        position: "absolute",
                        top: 4,
                        left: 4,
                        fontWeight: "bold",
                        zIndex: 1,
                        bgcolor: "background.paper",
                        px: 0.5,
                        borderRadius: 0.5,
                      }}
                    >
                      {day.format("D")}
                    </Typography>

                    {dayBuckets.length > 0 ? (
                      <Box sx={{ width: "100%", height: "100%" }}>
                        <CoverageStackedChart
                          buckets={
                            dayBuckets.length === 1
                              ? ([
                                  {
                                    ...dayBuckets[0],
                                    start: day.startOf("day").toISOString(),
                                    end: day.startOf("day").toISOString(),
                                  },
                                  {
                                    ...dayBuckets[0],
                                    start: day.endOf("day").toISOString(),
                                    end: day.endOf("day").toISOString(),
                                  },
                                ] as LabellingCoverageBucket[])
                              : dayBuckets
                          }
                          height={undefined}
                          showLegend={false}
                          showXAxis={false}
                          showYAxis={false}
                        />
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontSize="0.6rem"
                        >
                          {t("noData")}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          );
        })}
      </Box>
    </Stack>
  );
};
